import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getValidToken } from "../../../utils/tokenManager";
import axios from "axios";
import Button from "../../common/Button";
import warning from "../../../assets/agent/warning.svg";
import success from "../../../assets/agent/success.svg";
import fail from "../../../assets/agent/fail.svg";
import { useContactContext } from "../../../contexts/ContactContext";

function DeleteContactModal({ open, onClose, contact, onDeleteSuccess }) {
  const { refreshContacts } = useContactContext();
  const modalRef = useRef(null);
  const { t } = useTranslation();
  const contactId = contact?.contactId;

  const [successModal, setSuccessModal] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const [errors, setErrors] = useState({});

  const handleBackdropClick = (e) => {
    if (modalRef.current && e.target === modalRef.current) {
      onClose();
    }
  };

  const handleDeleteContact = async () => {
    try {
      const accessToken = await getValidToken();

      // Only Status is sent - this endpoint never touches any other
      // field on the contact (fixes the old bug where the full PUT wiped
      // out IsInterested/Source on every manual discard).
      const response = await axios.patch(
        `${process.env.REACT_APP_APIBASEURL}/api/contacts/${contactId}/status`,
        { Status: 0 },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        setSuccessModal(true);
        // Refresh contacts to update counts
        refreshContacts();
        if (onDeleteSuccess) {
          onDeleteSuccess();
        }
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || t("error.generic");
      setErrorModal(true);
      setErrors({ submit: errorMessage });
    }
  };

  useEffect(() => {
    if (successModal) {
      const timer = setTimeout(() => {
        setSuccessModal(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [successModal]);

  if (!open && !successModal && !errorModal) return null;

  return (
    <>
      {open && (
        <div
          ref={modalRef}
          onClick={handleBackdropClick}
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4"
        >
          <div className="bg-white rounded-2xl text-primary text-xl shadow-xl w-full max-w-[660px] p-7 relative flex flex-col items-center space-y-4">
            <img src={warning} alt="warning" className="w-[60px] h-[60px]" />
            <p className="mb-1 text-xl font-bold">{t("agent.dltMod.head")}</p>
            <div className="relative flex items-center">
              <span className="absolute left-0 w-1 h-1 bg-[#0A1E4C] rounded-full -ml-1"></span>
              <hr className="flex-1 border-t-2 border-[#0A1E4C] w-[350px]" />
              <span className="absolute right-0 w-1 h-1 bg-[#0A1E4C] rounded-full -mr-1"></span>
            </div>
            <p className="text-xl text-secondary font-bold text-center">
              {contact?.firstName} {contact?.lastName}
            </p>
            <p className="text-xl text-secondary text-center">
              {contact?.company}
            </p>
            <p className="text-xl w-[370px] text-center">
              {t("agent.dltMod.desc")}?
            </p>

            <div className="flex gap-4 flex-wrap justify-center">
              <button
                className="bg-gradient-to-r from-orange-400 to-orange-600 text-white px-4 py-2 rounded-lg w-full sm:w-48 h-[40px]"
                onClick={onClose}
              >
                {t("button.cancel")}
              </button>
              <Button
                variant="primary"
                className="w-full sm:w-48 px-4 py-2"
                onClick={handleDeleteContact}
              >
                {t("agent.noteInfo.yes")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {successModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4">
          <div className="bg-white rounded-lg text-primary text-xl shadow-xl w-full max-w-[660px] p-6 relative flex flex-col items-center">
            <img
              src={success}
              alt="success"
              className="w-[60px] h-[60px] mb-4"
            />
            <p className="mb-4 text-xl font-bold">
              {t("agent.dltMod.confirm.head")}
            </p>
            <div className="relative flex items-center mb-8">
              <span className="absolute left-0 w-1 h-1 bg-[#0A1E4C] rounded-full -ml-1"></span>
              <hr className="flex-1 border-t-2 border-[#0A1E4C] w-[350px]" />
              <span className="absolute right-0 w-1 h-1 bg-[#0A1E4C] rounded-full -mr-1"></span>
            </div>
            <p className="mb-6 text-xl">{t("agent.dltMod.confirm.desc")}</p>
          </div>
        </div>
      )}

      {errorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4">
          <div className="bg-white rounded-lg text-primary text-xl shadow-xl w-full max-w-[660px] p-6 relative flex flex-col items-center">
            <img src={fail} alt="fail" className="w-[60px] h-[60px] mb-4" />
            <p className="mb-4 text-xl font-bold">
              {t("admin.userError.head")}
            </p>
            <div className="relative flex items-center mb-8">
              <span className="absolute left-0 w-1 h-1 bg-[#0A1E4C] rounded-full -ml-1"></span>
              <hr className="flex-1 border-t-2 border-[#0A1E4C] w-[350px]" />
              <span className="absolute right-0 w-1 h-1 bg-[#0A1E4C] rounded-full -mr-1"></span>
            </div>
            {errors.submit && (
              <p className="mb-6 text-xl w-[455px] text-center">
                {errors.submit}
              </p>
            )}
            <Button variant="secondary" onClick={() => setErrorModal(false)}>
              {t("admin.userError.btn")}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

export default DeleteContactModal;